import { useEffect } from 'react';
import { X } from 'lucide-react';

export function ProductCountModal({ counts, error, loading, sort, onSort, page, onPage, onClose }) {
  useEffect(() => {
    const closeOnEscape = event => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3"
    role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <section role="dialog" aria-modal="true" aria-label="Conteo de productos filtrados"
      className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 p-4">
        <div>
          <h2 className="font-montserrat text-lg font-bold text-gray-800">Productos por tienda de los paquetes filtrados</h2>
          <p className="text-xs text-gray-500">Todos los paquetes que coinciden con Filtros, no solo la página visible. Es demanda del paquete, no stock disponible.</p>
        </div>
        <button type="button" autoFocus aria-label="Cerrar conteo de productos" onClick={onClose}
          className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"><X size={20}/></button>
      </div>
      <div className="min-h-0 overflow-y-auto p-4">
        {loading ? <p className="text-sm text-gray-500">Calculando productos…</p>
          : error ? <p role="alert" className="text-sm text-red-600">{error}</p>
          : !counts ? <p className="text-sm text-gray-500">El conteo aún no está disponible.</p>
          : <>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <p className="text-sm font-semibold text-gray-800">
                {counts.total_units} unidades · {counts.total_products} producto{counts.total_products === 1 ? '' : 's'} por tienda
              </p>
              <label className="text-xs font-semibold text-gray-600">Ordenar por
                <select aria-label="Ordenar conteo de productos" value={sort} onChange={event => onSort(event.target.value)}
                  className="ml-2 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm font-normal">
                  <option value="quantity">Mayor cantidad</option>
                  <option value="product">Producto</option>
                  <option value="packages">Más paquetes</option>
                </select>
              </label>
            </div>
            {counts.unidentified_packages > 0 && <p className="mt-3 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
              {counts.unidentified_packages} paquete{counts.unidentified_packages === 1 ? '' : 's'} con producto sin identificar. {counts.unidentified_units} unidades conocidas sin SKU; algunos paquetes antiguos podrían no tener cantidad registrada.
            </p>}
            {counts.legacy_assumed_units > 0 && <p className="mt-2 text-xs text-amber-700">
              {counts.legacy_assumed_units} paquete{counts.legacy_assumed_units === 1 ? '' : 's'} antiguo{counts.legacy_assumed_units === 1 ? '' : 's'} sin líneas de producto se contó como una unidad cada uno.
            </p>}
            {counts.rows.length === 0 ? <p className="mt-4 text-sm text-gray-500">No hay productos identificados con estos filtros.</p>
              : <div className="mt-3 overflow-x-auto rounded-lg border border-gray-100">
                <table className="w-full min-w-[540px] text-left text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500"><tr>
                    <th className="px-3 py-2">Producto</th><th className="px-3 py-2">Tienda</th><th className="px-3 py-2 text-right">Unidades</th>
                    <th className="px-3 py-2 text-right">Paquetes</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100">{counts.rows.map((row, index) => <tr key={`${row.product_id || 'sku'}:${row.owner_id || ''}:${row.sku || index}`}>
                    <td className="px-3 py-2"><span className="font-semibold text-gray-800">{row.product_title || row.sku || `Producto #${row.product_id}`}</span>
                      {row.sku && <span className="block text-xs text-gray-500">SKU {row.sku}</span>}</td>
                    <td className="px-3 py-2 text-gray-700">{row.username || (row.owner_id ? `Usuario #${row.owner_id}` : 'Sin tienda')}</td>
                    <td className="px-3 py-2 text-right font-bold text-gray-800">{row.units}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{row.packages}</td>
                  </tr>)}</tbody>
                </table>
              </div>}
            {counts.total_products > counts.per_page && <div className="mt-3 flex items-center justify-between gap-3 text-xs text-gray-600">
              <span>Productos {(page - 1) * counts.per_page + 1}–{Math.min(page * counts.per_page, counts.total_products)} de {counts.total_products}</span>
              <div className="flex gap-2">
                <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)} className="rounded border px-2 py-1 disabled:opacity-40">Anterior</button>
                <button type="button" disabled={!counts.has_next} onClick={() => onPage(page + 1)} className="rounded border px-2 py-1 disabled:opacity-40">Siguiente</button>
              </div>
            </div>}
          </>}
      </div>
    </section>
  </div>;
}
